import { segment } from '@actions/analytics';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import { noOp } from '@constants/empty';
import {
  SYLLABUS_SECTIONS,
  SYLLABUS_SECTION_VIEWED
} from '@cont/Compete/constants';
import useCompeteInfo from '@cont/Compete/useCompeteInfo';
import { getChapterCount } from '@cont/Compete/utils';
import styled from '@emotion/styled';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { useIsMobile } from '@stores/AppCommonData';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import Typography from 'aquilla/core/atoms/Typography';
import Button from 'aquilla/core/molecules/Button';
import Link from 'aquilla/core/molecules/Link';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import Month from './Month';
import SyllabusModal from './SyllabusModal';

const Wrapper = styled.div`
  margin-top: ${Spacings.SPACING_16B};

  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_10B};
    margin-bottom: ${Spacings.SPACING_10B};
  }
`;

const SubTitle = styled.div`
  font-weight: ${Fonts.REGULAR};
  font-size: ${Fonts.P1};
  line-height: 20px;
  color: var(--color-text-secondary);
  margin-top: ${Spacings.SPACING_1B};

  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.P2};
    line-height: 16px;
  }
`;

const MonthsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${Spacings.SPACING_6B};

  ${mediaQuery.uptoMobile} {
    flex-direction: column;
    height: 272px;
    width: 100%;
  }
`;

const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
`;

const StyledButton = styled(Button)`
  width: 74px;
  min-width: 74px;
  padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_3B};
  height: 32px;
  border: 1px solid var(--color-divider);
`;

const StyledLink = styled(Link)`
  svg {
    width: 24px;
  }
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-primary);
  font-weight: ${Fonts.SEMIBOLD};
  font-size: ${Fonts.P1};
  line-height: 20px;
  cursor: pointer;
  border: 1px solid var(--color-divider);
  margin-top: ${Spacings.SPACING_4B};
  height: 44px;
  width: 100%;
`;

const Syllabus = () => {
  const isMobile = useIsMobile();
  const { syllabus, mutate } = useCompeteInfo();
  const [isSyllabusModalOpen, setIsSyllabusModalOpen] = useState(false);
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const [isSeeAllClicked, setIsSeeAllClicked] = useState(false);
  const [selectedItem, setSelectedItem] = useState({});
  const { goalUID } = useRouter();
  const goalSegmentData = useGoalEventProps({ goalUID });

  const closeSyllabusModal = () => {
    setIsSyllabusModalOpen(false);
    setIsSeeAllClicked(false);
    setSelectedItem(null);
    document.body.style.overflow = 'unset';
  };

  const openSyllabusModal = () => {
    setIsSyllabusModalOpen(true);
  };

  useEffect(() => {
    mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  if (!syllabus) {
    return null;
  }

  const handleMonthClick = (syllabusData, index, count) => {
    if (count <= 0) {
      return noOp();
    }
    openSyllabusModal(index);
    setSelectedItem({
      type: SYLLABUS_SECTIONS.MONTH,
      data: syllabusData,
      level: 1
    });
    segment.track(SYLLABUS_SECTION_VIEWED, { ...goalSegmentData });
  };

  const renderMonths = () => {
    return syllabus?.map((syllabusData, index) => {
      const count = getChapterCount(syllabusData?.subjects) ?? 0;
      return (
        <Month
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          syllabus={syllabusData}
          handleMonthClick={() => {
            handleMonthClick(syllabusData, index, count);
          }}
        />
      );
    });
  };

  return (
    <>
      <Wrapper>
        <HeaderWrapper>
          <div>
            <Typography variant={isMobile ? 'h5' : 'h4'}>Syllabus</Typography>
            <SubTitle>New chapters every month</SubTitle>
          </div>
          {!isMobile && (
            <StyledButton
              size="small"
              variant="secondary"
              onClick={() => {
                setIsSeeAllClicked(true);
                setIsSyllabusModalOpen(true);
              }}
            >
              SEE ALL
            </StyledButton>
          )}
        </HeaderWrapper>
        <MonthsWrapper>{renderMonths()}</MonthsWrapper>
        <SyllabusModal
          isSyllabusModalOpen={isSyllabusModalOpen}
          closeSyllabusModal={closeSyllabusModal}
          isSeeAllClicked={isSeeAllClicked}
          selectedItem={selectedItem}
          setSelectedItem={setSelectedItem}
        />
        {isMobile && (
          <StyledLink
            rightArrow
            underline={false}
            onClick={(e) => {
              e.preventDefault();
              setIsSeeAllClicked(true);
              setIsSyllabusModalOpen(true);
            }}
            href="/"
          >
            <Typography variant="h6" color="var(--color-text-primary)">
              See all
            </Typography>
          </StyledLink>
        )}
      </Wrapper>
    </>
  );
};

export default Syllabus;
